"use client";

import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog as DialogPrimitive, VisuallyHidden } from "radix-ui";
import type { CSSProperties, ReactNode } from "react";
import { useCallback } from "react";
import { match } from "ts-pattern";
import { Wrap, cn, useRadixUIPortalFix } from "../../..";
import { ScreenOverlay } from "./Overlay";

export interface SideDialogProps
	extends Omit<DialogPrimitive.DialogProps, "open" | "onOpenChange"> {
	forceOpen?: boolean;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string | ReactNode;
	subtitle?: string | ReactNode;
	footer?: ReactNode;
	position?: "left" | "right";
	buttonPosition?: "left" | "right";
	size?: "sm" | "md" | "lg" | "xl";
	color?: "white" | "shades-light";
	className?: string;
	portal?: boolean;
}

export function SideDialog(props: SideDialogProps) {
	const {
		forceOpen = false,
		open,
		onOpenChange,
		defaultOpen,
		children,
		title,
		subtitle,
		footer,
		position = "right",
		buttonPosition = "right",
		size = "sm",
		color = "white",
		className,
		portal = true,
	} = props;

	const dialogSize = match(size)
		.with("sm", () => "24rem")
		.with("md", () => "32rem")
		.with("lg", () => "40rem")
		.with("xl", () => "64rem")
		.exhaustive();

	const isOpen = useRadixUIPortalFix({ defaultOpen, open });
	const onOpenChangeHandler = useCallback(
		(o: boolean) => {
			if (!forceOpen) {
				return onOpenChange(o);
			}
		},
		[forceOpen, onOpenChange],
	);

	return (
		<DialogPrimitive.Root open={isOpen} onOpenChange={(o) => onOpenChangeHandler(o)}>
			<Wrap
				if={portal}
				with={(children) => <DialogPrimitive.Portal>{children}</DialogPrimitive.Portal>}
			>
				<DialogPrimitive.Overlay
					asChild
					onPointerDown={(e) => {
						if (!e.currentTarget.nextElementSibling?.ariaHidden) {
							return onOpenChangeHandler(false);
						}
					}}
				>
					<ScreenOverlay variant="state" />
				</DialogPrimitive.Overlay>

				<DialogPrimitive.Content
					onPointerDownOutside={(e) => e.preventDefault()}
					style={{ "--dialog-x": dialogSize } as CSSProperties}
					className={cn(
						"fixed top-0 bottom-0 z-dialog w-screen max-w-[var(--dialog-x)] shadow-lg focus:outline-none",
						{
							"right-0 rdx-state-closed:animate-sideDialogOutFromRight rdx-state-open:animate-sideDialogInFromRight":
								position === "right",
							"left-0 rdx-state-closed:animate-sideDialogOutFromLeft rdx-state-open:animate-sideDialogInFromLeft":
								position === "left",
							"bg-white": color === "white",
							"bg-shades-light": color === "shades-light",
						},
					)}
				>
					<div className="inline-flex h-full w-full flex-col overflow-hidden">
						<div
							className={cn("mx-4 mt-4 mb-0 sm:mx-8 sm:mt-8", {
								"grid grid-cols-[2.5rem,auto] gap-x-2": buttonPosition === "left",
								"flex flex-row-reverse justify-between": buttonPosition === "right",
							})}
						>
							<DialogPrimitive.Close
								className={cn(
									{
										"hover:bg-shades-light focus-visible:bg-shades-light": color === "white",
										"hover:bg-shades-medium-light focus-visible:bg-shades-medium-light":
											color === "shades-light",
									},
									"flex h-8 w-8 shrink-0 items-center justify-center rounded p-1 text-2xl focus:outline-none motion-safe:transition sm:h-10 sm:w-10 sm:p-2",
								)}
							>
								<FontAwesomeIcon icon={faTimes} />
							</DialogPrimitive.Close>
							<div className="flex flex-col justify-center">
								<DialogPrimitive.Title className="font-bold font-primary text-lg leading-tight">
									{title}
								</DialogPrimitive.Title>

								{subtitle ? (
									<DialogPrimitive.Description className="mt-1 text-shades-dark leading-tight">
										{subtitle}
									</DialogPrimitive.Description>
								) : (
									<VisuallyHidden.Root asChild>
										<DialogPrimitive.Description asChild={typeof title !== "string"}>
											{title}
										</DialogPrimitive.Description>
									</VisuallyHidden.Root>
								)}
							</div>
						</div>

						<div
							className={cn("scrollbar-none relative my-4 h-full overflow-y-auto", {
								"sm:mb-8": !footer,
							})}
						>
							<div
								className={cn("mx-4 sm:mx-8", className, {
									"ml-14 sm:ml-20": buttonPosition === "left",
								})}
							>
								{children}
							</div>
						</div>

						{!!footer && <div className="mx-4 mb-4 sm:mx-8 sm:mb-8">{footer}</div>}
					</div>
				</DialogPrimitive.Content>
			</Wrap>
		</DialogPrimitive.Root>
	);
}
